import { useState } from 'react'
import { ChevronRight, ExternalLink, Layers, Bookmark, Lock } from 'lucide-react'
import type { HierarchyNodeResponse } from '@/features/syllabus/types'
import { visibleChildren } from '../utils/syllabusSubjects'

const NMC_SYLLABUS_PDF_URL =
  'https://www.nmc.org.in/MCIRest/open/getDocument?path=/Documents/Public/Portal/LatestNews/Public%20Notice_NEET_removed.pdf'

interface SyllabusUnitTreeProps {
  subject: HierarchyNodeResponse
  selectedNodeCode: string | null
  onSelect: (node: HierarchyNodeResponse) => void
  /** Free users: nodes show a lock, and selecting calls onSelect so the caller can show the unlock prompt */
  locked: boolean
}

interface NodeRowProps {
  node: HierarchyNodeResponse
  selected: boolean
  locked: boolean
  onSelect: (node: HierarchyNodeResponse) => void
}

function NodeLabel({ node, selected, locked, onSelect }: NodeRowProps) {
  const isUnit = node.nodeType === 'UNIT'
  const Icon = isUnit ? Layers : Bookmark
  const textColor = selected ? 'text-blue-700' : isUnit ? 'text-gray-900' : 'text-gray-700'

  return (
    <button
      type="button"
      onClick={() => onSelect(node)}
      aria-current={selected ? 'true' : undefined}
      className={`flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
        selected ? 'bg-blue-50' : 'hover:bg-gray-100'
      }`}
    >
      <Icon className={`h-4 w-4 flex-shrink-0 ${isUnit ? 'text-green-700' : 'text-gray-400'}`} />
      <span className={`min-w-0 flex-1 ${isUnit ? 'font-medium' : ''} ${textColor}`}>{node.nodeName}</span>
      {locked && <Lock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-label="Locked" />}
    </button>
  )
}

function UnitNode({
  unit,
  selectedNodeCode,
  locked,
  onSelect,
}: {
  unit: HierarchyNodeResponse
  selectedNodeCode: string | null
  locked: boolean
  onSelect: (node: HierarchyNodeResponse) => void
}) {
  const topics = visibleChildren(unit)
  // Open the unit holding the selected topic (e.g. when arriving from a shared link)
  const [isExpanded, setIsExpanded] = useState(() => topics.some((t) => t.nodeCode === selectedNodeCode))

  return (
    <li>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          disabled={topics.length === 0}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${unit.nodeName}`}
          className="flex-shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 disabled:invisible"
        >
          <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        </button>
        <NodeLabel
          node={unit}
          selected={unit.nodeCode === selectedNodeCode}
          locked={locked}
          onSelect={onSelect}
        />
      </div>

      {isExpanded && (
        <ul className="ml-7 border-l border-gray-100 pl-1">
          {topics.map((topic) => (
            <li key={topic.id}>
              <NodeLabel
                node={topic}
                selected={topic.nodeCode === selectedNodeCode}
                locked={locked}
                      onSelect={onSelect}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/** Unit → Topic tree for one subject; both levels are selectable */
export default function SyllabusUnitTree({ subject, selectedNodeCode, onSelect, locked }: SyllabusUnitTreeProps) {
  const units = visibleChildren(subject)

  if (units.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">No units found for {subject.nodeName}</p>
  }

  return (
    <>
      <ul className="space-y-0.5">
        {units.map((unit) => (
          <UnitNode
            key={unit.id}
            unit={unit}
            selectedNodeCode={selectedNodeCode}
            locked={locked}
            onSelect={onSelect}
          />
        ))}
      </ul>

      {/* Out of Syllabus nodes are hidden; point students to the source instead */}
      <p className="mt-3 border-t border-gray-100 px-2 pt-3 text-xs text-gray-500">
        <a
          href={NMC_SYLLABUS_PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
        >
          As per latest NMC Syllabus for NEET
          <ExternalLink size={12} />
        </a>
      </p>
    </>
  )
}
